import { ApolloCache } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Button, Stack, TextField } from "@mui/material";
import { ChangeEvent, useState } from "react";
import { DataLoader } from "../../components";
import { EmptyContainer, SuccessContainer } from "../../components/DataLoader";
import { EmptyContainer as Empty } from "../../components/ui/EmptyContainer";
import { Recipient, SaveEmailMatchMutation, EmailRecipientsDocument } from "../../graphql/types";
import type { EmailRecipientsQuery, EmailRecipientsQueryVariables } from "../../graphql/types";

import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { useDebounceSearchParams } from "../../lib/Hooks";
import { EmailRuleModal } from "./EmailRuleModal";
import { RecipientsList } from "./RecipientsList";

const EmailIngest = () => {
  const [inputValue, setInputValue] = useState("");
  const { data, loading, error } = useQuery<EmailRecipientsQuery, EmailRecipientsQueryVariables>(
    EmailRecipientsDocument
  );
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);

  const debouncedSearch = useDebounceSearchParams("search", inputValue, 1000);

  const filteredData = data?.recipients?.filter((recipient) =>
    recipient.email.toLowerCase().includes(debouncedSearch.get("search")?.toLowerCase() || "")
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const updateCache = (cache: ApolloCache, { data }: { data?: SaveEmailMatchMutation | null }) => {
    if (data?.save_emailmatch?.__typename === "SaveEmailMatchOk") {
      const updatedRules = data?.save_emailmatch;

      if (!updatedRules) return;
      cache.modify({
        fields: {
          recipients(existingRecipients = []) {
            if (existingRecipients.some((recipient: Recipient) => recipient.email === updatedRules.email)) {
              return existingRecipients.map((recipient: Recipient) => {
                if (recipient.email === updatedRules.email) {
                  return {
                    ...recipient,
                    rules: updatedRules.rules,
                  };
                }
                return recipient;
              });
            } else {
              existingRecipients = [...existingRecipients, updatedRules];
              return existingRecipients;
            }
          },
        },
      });
    }
  };

  const hasResults = (data?.recipients?.length || 0) > 0;
  return (
    <>
      <Stack
        sx={{ marginBottom: "16px", flexDirection: "row", justifyContent: "flex-end", alignItems: "center", gap: 4 }}
      >
        <TextField
          name="search"
          value={inputValue}
          onChange={handleSearch}
          size="small"
          placeholder="Search by email address..."
          sx={(theme) => ({
            backgroundColor: theme.palette.surfaceLight10.main,
          })}
        />
        <Button onClick={() => setShowAddRuleModal(true)} startIcon={<PlusIcon />} aria-label="Add recipient">
          Recipient
        </Button>
      </Stack>
      <DataLoader loading={loading} error={error} success={hasResults} defaults={{ small: false }}>
        <EmptyContainer>
          <Empty title="No Rules" />
        </EmptyContainer>
        <SuccessContainer>
          <RecipientsList recipients={filteredData} />
        </SuccessContainer>
      </DataLoader>
      <EmailRuleModal open={showAddRuleModal} onClose={() => setShowAddRuleModal(false)} updateCache={updateCache} />
    </>
  );
};

const EmailIngestWrapped = () => <EmailIngest />;

export { EmailIngestWrapped };
